import { AnimatePresence, motion } from "framer-motion";
import { isEmpty } from "lodash";
import { classNames } from "primereact/utils";
import { Ref } from "react";
import { useTranslation } from "react-i18next";
import { SearchInputText } from "../../../../../common/components/InputText";

export interface PanelTreeNode {
    children?: PanelTreeNode[];
    id: string;
    isActive?: boolean;
    isExpanded?: boolean;
    name: string;
}

interface PanelTemplateProps {
    onCompanySelect: (companyId: string) => void;
    onLocationSelect: (companyId: string, locationId: string) => void;
    onSearchChange: (value: string) => void;
    onToggleExpand: (companyId: string) => void;
    options: PanelTreeNode[];
    scrollContainerRef: Ref<HTMLDivElement>;
    search: string;
    selectedCompanyId?: string;
    selectedLocationId?: string;
}

const PanelTemplate = (props: PanelTemplateProps) => {
    const {
        onCompanySelect,
        onLocationSelect,
        onSearchChange,
        onToggleExpand,
        options,
        scrollContainerRef,
        search,
        selectedCompanyId,
        selectedLocationId,
    } = props;

    const { t } = useTranslation("common");

    // Callback ref to scroll the active node into view once layout is loaded
    const activeNodeRef = (node: HTMLDivElement | null) => {
        if (node) {
            requestAnimationFrame(() => {
                node.scrollIntoView({ block: "center", behavior: "auto" });
            });
        }
    };

    const isCompanyActive = (companyId: string) =>
        selectedCompanyId === companyId && !selectedLocationId;

    const isLocationActive = (companyId: string, locationId: string) =>
        selectedCompanyId === companyId && selectedLocationId === locationId;

    const activeNodeKey = selectedCompanyId
        ? selectedLocationId
            ? `location-${selectedCompanyId}-${selectedLocationId}`
            : `company-${selectedCompanyId}`
        : null;

    return (
        <div>
            <div className="p-3 bg-surface-3 border-b border-surface-5">
                <SearchInputText
                    className="w-full"
                    containerClassName="w-full"
                    isAnimated={false}
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                />
            </div>
            <div
                ref={scrollContainerRef}
                className="max-h-[50vh] overflow-y-auto"
            >
                {options.map((company) => (
                    <div key={`node-template-${company.id}`}>
                        <div
                            ref={
                                activeNodeKey === `company-${company.id}`
                                    ? activeNodeRef
                                    : null
                            }
                            className={classNames(
                                "flex gap-2 justify-between p-2 min-h-12 cursor-pointer hover:bg-surface-3 transition-colors duration-150 rounded-[6px] items-center",
                                {
                                    "!bg-primary-default/15": isCompanyActive(
                                        company.id
                                    ),
                                }
                            )}
                            onClick={() => onCompanySelect(company.id)}
                        >
                            <div className="flex items-center font-bold break-all">
                                <motion.i
                                    className={classNames(
                                        "w-8 p-2 text-surface-40 hover:text-default cursor-pointer transition-colors duration-150",
                                        {
                                            "pi pi-chevron-right": !isEmpty(
                                                company.children
                                            ),
                                        }
                                    )}
                                    animate={{
                                        rotate: company.isExpanded ? 90 : 0,
                                    }}
                                    transition={{
                                        duration: 0.1,
                                        ease: "easeInOut",
                                    }}
                                    initial={false}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleExpand(company.id);
                                    }}
                                />

                                {company.name}
                            </div>
                        </div>
                        <AnimatePresence initial={false}>
                            {company.isExpanded && (
                                <motion.div
                                    className="ml-4 overflow-hidden"
                                    initial={{
                                        height: 0,
                                        opacity: 0,
                                    }}
                                    animate={{
                                        height: "auto",
                                        opacity: 1,
                                    }}
                                    exit={{
                                        height: 0,
                                        opacity: 0,
                                    }}
                                    transition={{
                                        duration: 0.2,
                                        ease: "easeInOut",
                                    }}
                                >
                                    {company.children?.map((location) => {
                                        const locationActive = isLocationActive(
                                            company.id,
                                            location.id
                                        );
                                        const isActiveNode =
                                            activeNodeKey ===
                                            `location-${company.id}-${location.id}`;
                                        return (
                                            <div
                                                key={location.id}
                                                ref={
                                                    isActiveNode
                                                        ? activeNodeRef
                                                        : null
                                                }
                                                className={classNames(
                                                    "flex gap-2 justify-between p-2 pl-10 min-h-12 cursor-pointer hover:bg-surface-3 transition-colors duration-150 rounded-[6px] items-center break-all",
                                                    {
                                                        "!bg-primary-default/15":
                                                            locationActive,
                                                    }
                                                )}
                                                onClick={() =>
                                                    onLocationSelect(
                                                        company.id,
                                                        location.id
                                                    )
                                                }
                                            >
                                                {location.name}
                                            </div>
                                        );
                                    })}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
                {options.length === 0 && (
                    <div className="flex justify-center items-center h-12">
                        {t("common:noResultsFound")}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PanelTemplate;
